import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Plus, Trash2, Save, Package, HelpCircle, Settings, Loader2, ShoppingBag, Filter, Layers, X, Check, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { deriveTags, SIZE_OPTIONS, STYLE_OPTIONS, ROOM_OPTIONS } from "@/v2/lib/deriveTags";
import ImageUploader, { MultiImageUploader } from "@/components/admin/ImageUploader";
import SizeVariantsEditor, { sortVariants } from "@/components/admin/SizeVariantsEditor";

const TOKEN_KEY = "iryaa_admin_token";

// Categories are now fetched dynamically from the backend. This fallback is
// only used if the categories request fails.
const CATEGORY_FALLBACK = [
  { slug: "carpets", label: "Rugs & Carpets" },
  { slug: "rugs", label: "Jute Rugs" },
  { slug: "photo-frames", label: "Home Décor" },
  { slug: "kids-baskets", label: "Bags & Baskets" },
];

// Hook: subscribe to the shared list of categories, keeping every admin tab
// in sync when a new category is added.
function useCategories() {
  const [categories, setCategories] = useState(CATEGORY_FALLBACK);
  const [loaded, setLoaded] = useState(false);
  const reload = async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data.categories || []);
    } catch {}
    setLoaded(true);
  };
  useEffect(() => {
    reload();
    const onChange = () => reload();
    window.addEventListener("iryaa:categories-changed", onChange);
    return () => window.removeEventListener("iryaa:categories-changed", onChange);
  }, []);
  return { categories, loaded, reload };
}

function broadcastCategoriesChanged() {
  window.dispatchEvent(new Event("iryaa:categories-changed"));
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Verify the stored token on mount. If it's stale/expired, clear it so
  // the login screen shows instead of an empty dashboard.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.get("/admin/me").catch(() => {
      if (cancelled) return;
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      toast.error("Session expired. Please sign in again.");
    });
    return () => { cancelled = true; };
  }, [token]);

  // React to global 401s from any /admin/* call (fires from axios interceptor).
  useEffect(() => {
    const onUnauth = () => {
      setToken(null);
      toast.error("Session expired. Please sign in again.");
    };
    window.addEventListener("iryaa:admin-unauthorized", onUnauth);
    return () => window.removeEventListener("iryaa:admin-unauthorized", onUnauth);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-md border border-[#D8D4CC] p-10 bg-white">
          <div className="flex items-center gap-3 text-[#1B3B36]">
            <Lock size={16} />
            <span className="overline">Iryaa Homes · Studio</span>
          </div>
          <h1 className="mt-4 font-serif text-4xl text-[#1B3B36] leading-tight">Sign in</h1>
          <p className="mt-3 text-[13px] text-[#4A4A4A] font-light">Enter your studio password to manage the site.</p>
          <label className="block mt-8">
            <span className="overline">Password</span>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="mt-2 w-full border-0 border-b border-[#D8D4CC] bg-transparent py-3 text-[15px] focus:border-[#1B3B36] outline-none"
            />
          </label>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26] disabled:opacity-70"
          >
            {loading ? <><Loader2 className="animate-spin" size={14} /> Signing in…</> : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return <Dashboard onLogout={logout} />;
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("products");
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="border-b border-[#D8D4CC] bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <div className="overline">Iryaa Homes · Studio</div>
            <div className="font-serif text-2xl text-[#1B3B36] leading-tight mt-1">Manage your website</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-[12px] uppercase tracking-[0.18em] text-[#4A4A4A] hover:text-[#1B3B36]">
              View site ↗
            </a>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-[#1B3B36] text-[#1B3B36] px-4 py-2 text-[12px] uppercase tracking-[0.18em] hover:bg-[#1B3B36] hover:text-[#F9F8F6]">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex gap-1">
          {[
            { k: "products", label: "Products", icon: Package },
            { k: "mapping", label: "Filter Mapping", icon: Filter },
            { k: "categories", label: "Categories", icon: Layers },
            { k: "page-images", label: "Page Images", icon: ImageIcon },
            { k: "orders", label: "Orders", icon: ShoppingBag },
            { k: "faqs", label: "FAQs", icon: HelpCircle },
            { k: "settings", label: "Site Settings", icon: Settings },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-5 py-3 text-[12px] uppercase tracking-[0.18em] border-b-2 -mb-px ${
                tab === t.k ? "border-[#1B3B36] text-[#1B3B36]" : "border-transparent text-[#84968C] hover:text-[#1B3B36]"
              }`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 md:px-10 py-10">
        {tab === "products" && <ProductsTab />}
        {tab === "mapping" && <MappingTab />}
        {tab === "categories" && <CategoriesTab />}
        {tab === "page-images" && <PageImagesTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "faqs" && <FaqsTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}

// ==================== PRODUCTS ====================

const emptyProduct = () => ({
  id: `p_${Date.now()}`,
  name: "",
  category: "carpets",
  material: "wool",
  price_from: 0,
  mrp: 0,
  image: "",
  gallery: [],
  story: "",
  dimensions: "",
  features: [],
  care: [],
  buy_url: "",
  order: 999,
  tags_sizes: [],
  tags_styles: [],
  tags_rooms: [],
});

function ProductsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setItems(data.products || []);
    } catch (e) {
      toast.error("Could not load products");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    try {
      if (items.find((x) => x.id === p.id)) {
        await api.put(`/admin/products/${p.id}`, p);
      } else {
        await api.post("/admin/products", p);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product? It will disappear from the site immediately.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (loading) return <Loader2 className="animate-spin" />;

  if (editing) {
    return <ProductForm product={editing} onSave={save} onCancel={() => setEditing(null)} />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-serif text-3xl text-[#1B3B36]">Products ({items.length})</h2>
        <button
          data-testid="admin-add-product"
          onClick={() => setEditing(emptyProduct())}
          className="inline-flex items-center gap-2 bg-[#1B3B36] text-[#F9F8F6] px-5 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-[#132A26]"
        >
          <Plus size={13} /> Add product
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="border border-[#D8D4CC] bg-white flex flex-col">
            <div className="aspect-[4/3] bg-[#EFECE5] overflow-hidden">
              {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="overline text-[10px]">{p.category} · {p.material}</div>
              <div className="font-serif text-xl text-[#1B3B36] mt-1 leading-tight">{p.name || "(untitled)"}</div>
              <div className="mt-2 text-[13px]">
                ₹{Number(p.price_from).toLocaleString("en-IN")}
                {p.mrp ? <span className="line-through text-[#84968C] ml-2">₹{Number(p.mrp).toLocaleString("en-IN")}</span> : null}
              </div>
              <div className="mt-auto pt-4 flex gap-2">
                <button
                  onClick={() => setEditing({ ...p })}
                  data-testid={`admin-edit-${p.id}`}
                  className="flex-1 text-[11px] uppercase tracking-[0.18em] border border-[#1B3B36] text-[#1B3B36] py-2 hover:bg-[#1B3B36] hover:text-[#F9F8F6]"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`admin-delete-${p.id}`}
                  className="text-[11px] uppercase tracking-[0.18em] border border-[#B84A3E] text-[#B84A3E] py-2 px-3 hover:bg-[#B84A3E] hover:text-white"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductForm({ product, onSave, onCancel }) {
  const [p, setP] = useState({
    ...product,
    tags_sizes: product.tags_sizes || [],
    tags_styles: product.tags_styles || [],
    tags_rooms: product.tags_rooms || [],
    variants: product.variants || [],
  });
  const [preset, setPreset] = useState(/jute/i.test(`${product.material} ${product.category}`) ? "jute" : "tufted");
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const buildPayload = () => {
    const variants = sortVariants(p.variants, preset).map((v) => ({
      size: v.size, enabled: !!v.enabled, price: Number(v.price) || 0, mrp: v.mrp ? Number(v.mrp) : null, stock: Number(v.stock) || 0,
    }));
    const first = variants.find((v) => v.enabled);
    return {
      ...p, variants, order: Number(p.order),
      price_from: first ? first.price : Number(p.price_from),
      mrp: first ? first.mrp : (p.mrp ? Number(p.mrp) : null),
    };
  };
  const setList = (k, s) => set(k, s.split("\n").map((l) => l.trim()).filter(Boolean));
  const toggleTag = (k, val) => setP((x) => {
    const arr = x[k] || [];
    return { ...x, [k]: arr.includes(val) ? arr.filter((t) => t !== val) : [...arr, val] };
  });

  const { categories, reload: reloadCategories } = useCategories();
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");

  const suggested = useMemo(() => deriveTags({ ...p, tags_sizes: [], tags_styles: [], tags_rooms: [] }), [p]);

  const addNewCategory = async () => {
    const label = newCatLabel.trim();
    if (!label) return;
    try {
      const { data } = await api.post("/admin/categories", { slug: "", label, order: categories.length + 1 });
      toast.success(`Category "${data.label}" added`);
      set("category", data.slug);
      setNewCatLabel("");
      setShowNewCat(false);
      broadcastCategoriesChanged();
      reloadCategories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add category");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-serif text-3xl text-[#1B3B36]">Edit product</h2>
        <div className="flex gap-2">
          <button onClick={onCancel} className="text-[12px] uppercase tracking-[0.18em] border border-[#84968C] text-[#4A4A4A] px-5 py-3">
            Cancel
          </button>
          <button
            data-testid="admin-save-product"
            onClick={() => onSave(buildPayload())}
            className="inline-flex items-center gap-2 bg-[#1B3B36] text-[#F9F8F6] px-5 py-3 text-[12px] uppercase tracking-[0.18em]"
          >
            <Save size={13} /> Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl">
        <Field label="Name"><input value={p.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" className={inp} /></Field>
        <Field label="Category">
          <div className="flex gap-2 mt-2">
            <select value={p.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category" className={inp + " flex-1 mt-0"}>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label} ({c.slug})</option>)}
            </select>
            <button
              type="button"
              onClick={() => setShowNewCat((v) => !v)}
              data-testid="pf-add-category-toggle"
              className="text-[11px] uppercase tracking-[0.18em] border border-[#1B3B36] text-[#1B3B36] px-3 hover:bg-[#1B3B36] hover:text-[#F9F8F6]"
            >{showNewCat ? "Cancel" : "+ New"}</button>
          </div>
          {showNewCat && (
            <div className="mt-3 flex gap-2" data-testid="pf-new-category-inline">
              <input
                value={newCatLabel}
                onChange={(e) => setNewCatLabel(e.target.value)}
                placeholder="e.g. Wall Hangings"
                data-testid="pf-new-category-label"
                className="flex-1 border border-[#D8D4CC] bg-white px-3 py-2 text-[14px] outline-none focus:border-[#1B3B36]"
              />
              <button
                type="button"
                onClick={addNewCategory}
                data-testid="pf-new-category-save"
                className="inline-flex items-center gap-1 bg-[#1B3B36] text-[#F9F8F6] px-3 text-[11px] uppercase tracking-[0.18em]"
              ><Check size={13} /> Add</button>
            </div>
          )}
        </Field>
        <Field label="Material"><input value={p.material} onChange={(e) => set("material", e.target.value)} className={inp} /></Field>
        <Field label="Order (lower = shown first)"><input type="number" value={p.order} onChange={(e) => set("order", e.target.value)} className={inp} /></Field>
        <Field label="Price (₹)"><input type="number" value={p.price_from} onChange={(e) => set("price_from", e.target.value)} data-testid="pf-price" className={inp} /></Field>
        <Field label="MRP / Strike-through price (₹)"><input type="number" value={p.mrp || ""} onChange={(e) => set("mrp", e.target.value)} className={inp} /></Field>
        <Field label="Amazon buy URL" span2><input value={p.buy_url || ""} onChange={(e) => set("buy_url", e.target.value)} className={inp} /></Field>
        <Field label="Dimensions" span2><input value={p.dimensions || ""} onChange={(e) => set("dimensions", e.target.value)} className={inp} placeholder="e.g. 4 × 6 ft (122 × 189 cm)" /></Field>

        <SizeVariantsEditor
          variants={p.variants}
          preset={preset}
          onPresetChange={setPreset}
          onChange={(v) => set("variants", v)}
          defaultPrice={Number(p.price_from)}
          defaultMrp={p.mrp ? Number(p.mrp) : ""}
        />

        <div className="lg:col-span-2 border-t border-[#EAE7DF] pt-6 mt-2" data-testid="pf-images-section">
          <div className="overline mb-2">Product images</div>
          <p className="text-[12px] text-[#84968C] font-light mb-4">
            Upload images directly from your computer. The main image appears on the product card; the gallery is used on the product page. Drag the thumbnails using the arrows to reorder, click Replace to swap, or ✕ to remove.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <ImageUploader
                testid="pf-main-image"
                label="Main image"
                value={p.image || ""}
                onChange={(url) => set("image", url)}
                aspect="aspect-[4/5]"
              />
            </div>
            <div className="md:col-span-2">
              <MultiImageUploader
                testid="pf-gallery"
                label="Gallery images"
                value={p.gallery || []}
                onChange={(list) => set("gallery", list)}
                maxCount={12}
              />
            </div>
          </div>
        </div>


        {/* Manual tag pickers — override the auto-derived tags */}
        <div className="lg:col-span-2 border-t border-[#EAE7DF] pt-6 mt-2" data-testid="pf-tags-section">
          <div className="overline mb-2">Filter tags (manual override)</div>
          <p className="text-[12px] text-[#84968C] font-light mb-4">
            Manually set which Size / Style / Room filters this product appears under. Leaving a row empty falls back to auto-derived tags computed from the name and dimensions.
          </p>
          <TagPicker
            testid="pf-tags-sizes"
            label="Sizes"
            options={SIZE_OPTIONS}
            value={p.tags_sizes}
            auto={suggested._auto.sizes}
            onToggle={(v) => toggleTag("tags_sizes", v)}
            onUseAuto={() => set("tags_sizes", [...suggested._auto.sizes])}
            onClear={() => set("tags_sizes", [])}
          />
          <TagPicker
            testid="pf-tags-styles"
            label="Styles"
            options={STYLE_OPTIONS}
            value={p.tags_styles}
            auto={suggested._auto.styles}
            onToggle={(v) => toggleTag("tags_styles", v)}
            onUseAuto={() => set("tags_styles", [...suggested._auto.styles])}
            onClear={() => set("tags_styles", [])}
          />
          <TagPicker
            testid="pf-tags-rooms"
            label="Rooms"
            options={ROOM_OPTIONS}
            value={p.tags_rooms}
            auto={suggested._auto.rooms}
            onToggle={(v) => toggleTag("tags_rooms", v)}
            onUseAuto={() => set("tags_rooms", [...suggested._auto.rooms])}
            onClear={() => set("tags_rooms", [])}
          />
        </div>

        <Field label="Story / description" span2><textarea rows={3} value={p.story} onChange={(e) => set("story", e.target.value)} className={inp} /></Field>
        <Field label="Features (one per line)" span2>
          <textarea rows={4} value={(p.features || []).join("\n")} onChange={(e) => setList("features", e.target.value)} className={inp} />
        </Field>
        <Field label="Care instructions (one per line)" span2>
          <textarea rows={4} value={(p.care || []).join("\n")} onChange={(e) => setList("care", e.target.value)} className={inp} />
        </Field>
      </div>
    </div>
  );
}

function TagPicker({ testid, label, options, value, auto, onToggle, onUseAuto, onClear }) {
  const chosen = value || [];
  const isManual = chosen.length > 0;
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px] uppercase tracking-[0.18em] text-[#1B3B36] font-medium">{label}</div>
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em]">
          {isManual && <span className="text-[#B85742]">Manual override active</span>}
          {auto && auto.length > 0 && (
            <button type="button" onClick={onUseAuto} className="v2-link text-[#4A4A4A] hover:text-[#1B3B36]">Use suggested ({auto.length})</button>
          )}
          {isManual && (
            <button type="button" onClick={onClear} className="text-[#B85742] hover:underline">Clear</button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = chosen.includes(opt);
          const suggested = auto && auto.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              data-testid={`${testid}-${opt.replace(/[^a-z0-9]+/gi, "-")}`}
              className={`text-[12px] px-3 py-1.5 border transition-colors ${
                active
                  ? "bg-[#1B3B36] text-[#F9F8F6] border-[#1B3B36]"
                  : suggested
                  ? "border-[#B85742] text-[#B85742] hover:bg-[#B85742] hover:text-white"
                  : "border-[#D8D4CC] text-[#4A4A4A] hover:border-[#1B3B36] hover:text-[#1B3B36]"
              }`}
            >
              {opt}{suggested && !active ? " ·" : ""}
            </button>
          );
        })}
      </div>
      {!isManual && auto && auto.length === 0 && (
        <p className="mt-2 text-[11px] text-[#B0AFA8] italic">No auto-tags detected — this product won't appear under any {label.toLowerCase()} filter until you tag it.</p>
      )}
    </div>
  );
}

// ==================== ORDERS ====================

const STATUSES = ["pending_payment", "paid", "processing", "shipped", "delivered", "cancelled", "refunded"];
const STATUS_LABEL = {
  pending_payment: "Pending payment",
  paid: "Paid",
  processing: "Processing",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  refunded: "Refunded",
};
const STATUS_COLOR = {
  pending_payment: "bg-[#EFECE5] text-[#4A4A4A]",
  paid: "bg-[#1B3B36] text-[#F9F8F6]",
  processing: "bg-[#84968C] text-[#F9F8F6]",
  shipped: "bg-[#4A4A4A] text-[#F9F8F6]",
  delivered: "bg-[#1B3B36] text-[#F9F8F6]",
  cancelled: "bg-[#B84A3E] text-white",
  refunded: "bg-[#B84A3E] text-white",
};

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data.orders || []);
    } catch {
      toast.error("Could not load orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateOrder = async (id, patch) => {
    try {
      await api.put(`/admin/orders/${id}`, patch);
      toast.success("Order updated");
      load();
      if (selected?.id === id) setSelected({ ...selected, ...patch });
    } catch {
      toast.error("Update failed");
    }
  };

  if (loading) return <Loader2 className="animate-spin" />;

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  if (selected) {
    return <OrderDetail order={selected} onBack={() => setSelected(null)} onUpdate={updateOrder} />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-serif text-3xl text-[#1B3B36]">Orders ({orders.length})</h2>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            data-testid={`admin-orders-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] border ${
              filter === s ? "bg-[#1B3B36] text-[#F9F8F6] border-[#1B3B36]" : "border-[#D8D4CC] text-[#4A4A4A] hover:border-[#1B3B36]"
            }`}
          >
            {s === "all" ? "All" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-[#D8D4CC] bg-white p-16 text-center text-[#4A4A4A]">
          <ShoppingBag size={24} className="mx-auto text-[#84968C]" />
          <div className="mt-4 font-serif text-2xl text-[#1B3B36]">No orders yet.</div>
          <p className="mt-2 text-[13px] font-light">They'll appear here as customers check out.</p>
        </div>
      ) : (
        <div className="border border-[#D8D4CC] bg-white overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[#EFECE5] text-[11px] uppercase tracking-[0.18em] text-[#4A4A4A]">
              <tr>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Placed</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D8D4CC]">
              {filtered.map((o) => (
                <tr key={o.id} data-testid={`admin-order-row-${o.id}`} className="hover:bg-[#F9F8F6]">
                  <td className="px-4 py-3 font-mono text-[12px]">#{o.id.slice(0, 8)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1B3B36]">{o.customer.name}</div>
                    <div className="text-[11px] text-[#84968C]">{o.customer.email}</div>
                  </td>
                  <td className="px-4 py-3">{o.items.reduce((s, i) => s + i.quantity, 0)} pieces</td>
                  <td className="px-4 py-3 text-right font-serif text-[15px]">₹{o.total.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider ${STATUS_COLOR[o.status]}`}>
                      {STATUS_LABEL[o.status] || o.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-[#4A4A4A]">{o.created_at.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(o)} data-testid={`admin-order-open-${o.id}`} className="text-[11px] uppercase tracking-[0.18em] link-underline text-[#1B3B36]">Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrderDetail({ order, onBack, onUpdate }) {
  const [status, setStatus] = useState(order.status);
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [note, setNote] = useState(order.admin_note || "");

  return (
    <div>
      <button onClick={onBack} className="text-[12px] uppercase tracking-[0.18em] link-underline text-[#4A4A4A] mb-6">← Back to orders</button>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 border border-[#D8D4CC] bg-white p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="overline">Order</div>
              <div className="font-serif text-3xl text-[#1B3B36] mt-1">#{order.id.slice(0, 8)}</div>
              <div className="text-[12px] text-[#84968C] mt-1">Placed {order.created_at.slice(0, 16).replace("T", " · ")}</div>
            </div>
            <span className={`inline-block px-3 py-1 text-[11px] uppercase tracking-wider ${STATUS_COLOR[order.status]}`}>
              {STATUS_LABEL[order.status]}
            </span>
          </div>

          <div className="mt-8 overline">Items</div>
          <div className="mt-3 divide-y divide-[#D8D4CC]">
            {order.items.map((i) => (
              <div key={`${i.product_id}-${i.size || ""}`} className="flex gap-3 py-3">
                <img src={i.image} alt="" className="w-14 h-16 object-cover bg-[#EFECE5]" />
                <div className="flex-1 text-[13px]">
                  <div className="font-serif text-[15px] text-[#1B3B36]">{i.name}</div>
                  {i.size && <div className="text-[#4A4A4A]" data-testid={`admin-order-item-size-${i.product_id}-${i.size.replace(/[^0-9a-z]/gi, "-")}`}>Size: {i.size} ft</div>}
                  <div className="text-[#4A4A4A]">Qty {i.quantity} × ₹{i.price.toLocaleString("en-IN")}</div>
                </div>
                <div className="text-[14px] text-[#1B3B36]">₹{(i.price * i.quantity).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>

          <dl className="mt-4 border-t border-[#D8D4CC] pt-4 space-y-1 text-[13px] text-[#4A4A4A]">
            <div className="flex justify-between"><dt>Subtotal</dt><dd>₹{order.subtotal.toLocaleString("en-IN")}</dd></div>
            <div className="flex justify-between"><dt>Shipping</dt><dd>{order.shipping === 0 ? "Free" : `₹${order.shipping}`}</dd></div>
            <div className="flex justify-between font-serif text-lg text-[#1B3B36] pt-2 border-t border-[#D8D4CC]"><dt>Total</dt><dd>₹{order.total.toLocaleString("en-IN")}</dd></div>
          </dl>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 text-[13px]">
            <div>
              <div className="overline">Customer</div>
              <div className="mt-2 text-[#4A4A4A] font-light leading-relaxed">
                {order.customer.name}<br />
                {order.customer.email}<br />
                {order.customer.phone}
              </div>
            </div>
            <div>
              <div className="overline">Shipping to</div>
              <div className="mt-2 text-[#4A4A4A] font-light leading-relaxed">
                {order.shipping_address.line1}{order.shipping_address.line2 ? <>, {order.shipping_address.line2}</> : null}<br />
                {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.pincode}<br />
                {order.shipping_address.country}
              </div>
            </div>
          </div>
          {order.notes && (
            <div className="mt-6">
              <div className="overline">Customer notes</div>
              <p className="mt-2 text-[13px] text-[#4A4A4A] font-light">{order.notes}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-4 border border-[#D8D4CC] bg-white p-6 h-fit">
          <div className="overline">Update status</div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-order-status-select" className={`mt-3 ${inp}`}>
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>

          <div className="mt-6 overline">Tracking number</div>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="admin-order-tracking" placeholder="e.g. DTDC 1234567890" className={`mt-3 ${inp}`} />

          <div className="mt-6 overline">Internal note</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="For your team only." className={`mt-3 ${inp}`} />

          <button
            onClick={() => onUpdate(order.id, { status, tracking_number: tracking, admin_note: note })}
            data-testid="admin-order-save"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1B3B36] text-[#F9F8F6] px-5 py-3 text-[12px] uppercase tracking-[0.18em]"
          >
            <Save size={13} /> Save changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== FAQs ====================

function FaqsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/faqs");
    setItems(data.faqs || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = () => setItems([...items, { id: `fq_${Date.now()}`, q: "", a: "", order: items.length + 1 }]);
  const update = (i, k, v) => setItems(items.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const save = async (i) => {
    const it = items[i];
    try { await api.put(`/admin/faqs/${it.id}`, { ...it, order: Number(it.order) }); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
  };
  const remove = async (i) => {
    const it = items[i];
    if (!window.confirm("Delete this FAQ?")) return;
    try { await api.delete(`/admin/faqs/${it.id}`); load(); } catch { toast.error("Delete failed"); }
  };

  if (loading) return <Loader2 className="animate-spin" />;
  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-serif text-3xl text-[#1B3B36]">FAQs ({items.length})</h2>
        <button onClick={add} data-testid="admin-add-faq" className="inline-flex items-center gap-2 bg-[#1B3B36] text-[#F9F8F6] px-5 py-3 text-[12px] uppercase tracking-[0.18em]">
          <Plus size={13} /> Add FAQ
        </button>
      </div>
      <div className="space-y-6 max-w-4xl">
        {items.map((f, i) => (
          <div key={f.id} className="border border-[#D8D4CC] bg-white p-6">
            <Field label="Question"><input value={f.q} onChange={(e) => update(i, "q", e.target.value)} className={inp} /></Field>
            <div className="mt-4"><Field label="Answer"><textarea rows={3} value={f.a} onChange={(e) => update(i, "a", e.target.value)} className={inp} /></Field></div>
            <div className="mt-4 flex gap-3 items-end">
              <div className="flex-1 max-w-[140px]"><Field label="Order"><input type="number" value={f.order} onChange={(e) => update(i, "order", e.target.value)} className={inp} /></Field></div>
              <button onClick={() => save(i)} data-testid={`admin-faq-save-${i}`} className="inline-flex items-center gap-2 bg-[#1B3B36] text-[#F9F8F6] px-4 py-3 text-[11px] uppercase tracking-[0.18em]">
                <Save size={12} /> Save
              </button>
              <button onClick={() => remove(i)} className="border border-[#B84A3E] text-[#B84A3E] px-3 py-3">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== SITE SETTINGS ====================

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/site-settings").then((r) => setS(r.data));
  }, []);

  if (!s) return <Loader2 className="animate-spin" />;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/site-settings", s); toast.success("Site settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="font-serif text-3xl text-[#1B3B36] mb-8">Site settings</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Brand name"><input value={s.brand_name} onChange={(e) => set("brand_name", e.target.value)} className={inp} /></Field>
        <Field label="Tagline"><input value={s.tagline} onChange={(e) => set("tagline", e.target.value)} className={inp} /></Field>
        <Field label="WhatsApp number (digits only, incl. country code)" span2>
          <input value={s.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} className={inp} placeholder="e.g. 918971188977" />
        </Field>
        <Field label="Contact email"><input value={s.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inp} /></Field>
        <Field label="Contact phone"><input value={s.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className={inp} /></Field>
        <Field label="Studio address — line 1"><input value={s.studio_address_line_1} onChange={(e) => set("studio_address_line_1", e.target.value)} className={inp} /></Field>
        <Field label="Studio address — line 2"><input value={s.studio_address_line_2} onChange={(e) => set("studio_address_line_2", e.target.value)} className={inp} /></Field>
        <Field label="Instagram URL"><input value={s.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} className={inp} /></Field>
        <Field label="Facebook URL"><input value={s.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} className={inp} /></Field>
        <Field label="Announcement bar (top strip on v2)" span2>
          <input value={s.announcement_message || ""} onChange={(e) => set("announcement_message", e.target.value)} data-testid="admin-announcement-input" className={inp} placeholder="Handmade in India · Custom sizes available · Worldwide enquiries welcome" />
        </Field>
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-save-settings" className="mt-8 inline-flex items-center gap-2 bg-[#1B3B36] text-[#F9F8F6] px-6 py-3 text-[12px] uppercase tracking-[0.18em]">
        {saving ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save settings
      </button>
    </div>
  );
}

// ==================== SHARED ====================

const inp = "mt-2 w-full border border-[#D8D4CC] bg-transparent px-3 py-2 text-[14px] focus:border-[#1B3B36] outline-none";

function Field({ label, children, span2 }) {
  return (
    <label className={`block ${span2 ? "md:col-span-2" : ""}`}>
      <span className="overline">{label}</span>
      {children}
    </label>
  );
}

// ==================== FILTER MAPPING TAB ====================

function MappingTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const { categories } = useCategories();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data.products || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const persistTags = async (product, next) => {
    setSavingId(product.id);
    const merged = { ...product, ...next };
    try {
      await api.put(`/admin/products/${product.id}`, merged);
      setProducts((list) => list.map((x) => (x.id === product.id ? merged : x)));
      toast.success("Tags saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const rows = useMemo(() => {
    return products
      .map((p) => ({ product: p, tags: deriveTags(p) }))
      .filter(({ product, tags }) => {
        if (catFilter !== "all" && product.category !== catFilter) return false;
        if (q && !product.name.toLowerCase().includes(q.toLowerCase())) return false;
        if (tagFilter) {
          const all = [...tags.sizes, ...tags.styles, ...tags.rooms, tags.material].filter(Boolean);
          if (!all.some((t) => t === tagFilter)) return false;
        }
        return true;
      });
  }, [products, q, catFilter, tagFilter]);

  const stats = useMemo(() => {
    const s = { sizes: {}, styles: {}, rooms: {}, materials: {} };
    products.forEach((p) => {
      const t = deriveTags(p);
      t.sizes.forEach((x) => (s.sizes[x] = (s.sizes[x] || 0) + 1));
      t.styles.forEach((x) => (s.styles[x] = (s.styles[x] || 0) + 1));
      t.rooms.forEach((x) => (s.rooms[x] = (s.rooms[x] || 0) + 1));
      if (t.material) s.materials[t.material] = (s.materials[t.material] || 0) + 1;
    });
    return s;
  }, [products]);

  if (loading) return <div className="py-24 text-center text-[#84968C]"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div data-testid="admin-mapping-tab">
      <div className="mb-8">
        <div className="overline">Filter Mapping</div>
        <h2 className="mt-2 font-serif text-3xl text-[#1B3B36]">How your products map to shop filters</h2>
        <p className="mt-2 text-[13px] text-[#4A4A4A] font-light max-w-2xl">
          Tags are auto-derived from each product's name, material and dimensions. Click <strong>Edit tags</strong> on any row to override them manually, or open the full product form for larger edits. Manual overrides show a <span className="text-[#B85742] font-medium">•</span> next to the row.
        </p>
      </div>

      {/* Coverage summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {[
          { title: "By Size", data: stats.sizes, options: SIZE_OPTIONS },
          { title: "By Style", data: stats.styles, options: STYLE_OPTIONS },
          { title: "By Room", data: stats.rooms, options: ROOM_OPTIONS },
          { title: "By Material", data: stats.materials, options: Object.keys(stats.materials) },
        ].map((g) => (
          <div key={g.title} className="border border-[#D8D4CC] p-4 bg-white">
            <div className="overline mb-3">{g.title}</div>
            <div className="flex flex-wrap gap-1.5">
              {g.options.map((opt) => {
                const count = g.data[opt] || 0;
                return (
                  <button
                    key={opt}
                    onClick={() => setTagFilter(tagFilter === opt ? "" : opt)}
                    className={`text-[11px] px-2 py-1 border ${
                      tagFilter === opt
                        ? "bg-[#1B3B36] text-[#F9F8F6] border-[#1B3B36]"
                        : count === 0
                        ? "border-dashed border-[#D8D4CC] text-[#B0AFA8]"
                        : "border-[#D8D4CC] text-[#1B3B36] hover:border-[#1B3B36]"
                    }`}
                  >
                    {opt} <span className="opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search product name…"
          data-testid="admin-mapping-search"
          className="border border-[#D8D4CC] px-3 py-2 text-[13px] w-72 outline-none focus:border-[#1B3B36]"
        />
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          data-testid="admin-mapping-cat"
          className="border border-[#D8D4CC] px-3 py-2 text-[13px] bg-white outline-none focus:border-[#1B3B36]"
        >
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
        </select>
        {tagFilter && (
          <button
            onClick={() => setTagFilter("")}
            className="text-[12px] text-[#B85742] uppercase tracking-[0.18em]"
          >Clear tag filter · {tagFilter}</button>
        )}
        <div className="ml-auto text-[12px] text-[#4A4A4A]">{rows.length} of {products.length} products</div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-[#D8D4CC] bg-white">
        <table className="w-full text-[13px]" data-testid="admin-mapping-table">
          <thead className="bg-[#F0EDE5] text-[11px] uppercase tracking-[0.18em] text-[#4A4A4A]">
            <tr>
              <th className="text-left px-3 py-3">Product</th>
              <th className="text-left px-3 py-3">Category</th>
              <th className="text-left px-3 py-3">Material</th>
              <th className="text-left px-3 py-3">Dimensions</th>
              <th className="text-left px-3 py-3">Sizes</th>
              <th className="text-left px-3 py-3">Styles</th>
              <th className="text-left px-3 py-3">Rooms</th>
              <th className="text-right px-3 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ product, tags }) => {
              const isEditing = editing === product.id;
              const isSaving = savingId === product.id;
              const manualDot = (tags.manual.sizes || tags.manual.styles || tags.manual.rooms);
              return (
                <tr key={product.id} data-testid={`admin-mapping-row-${product.id}`} className="border-t border-[#EAE7DF] align-top">
                  <td className="px-3 py-3 max-w-md">
                    <div className="font-medium text-[#1B3B36]">
                      {manualDot && <span className="text-[#B85742] mr-1" title="Has manual tag overrides">•</span>}
                      {product.name}
                    </div>
                    <div className="text-[11px] text-[#84968C] mt-0.5">{product.id}</div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">{product.category}</td>
                  <td className="px-3 py-3">{product.material || <span className="text-[#B0AFA8]">—</span>}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{product.dimensions || <span className="text-[#B0AFA8]">—</span>}</td>
                  {isEditing ? (
                    <>
                      <td className="px-3 py-3"><InlineTagEditor testid={`mt-sizes-${product.id}`} options={SIZE_OPTIONS} value={product.tags_sizes || []} onChange={(v) => persistTags(product, { tags_sizes: v })} disabled={isSaving} /></td>
                      <td className="px-3 py-3"><InlineTagEditor testid={`mt-styles-${product.id}`} options={STYLE_OPTIONS} value={product.tags_styles || []} onChange={(v) => persistTags(product, { tags_styles: v })} disabled={isSaving} /></td>
                      <td className="px-3 py-3"><InlineTagEditor testid={`mt-rooms-${product.id}`} options={ROOM_OPTIONS} value={product.tags_rooms || []} onChange={(v) => persistTags(product, { tags_rooms: v })} disabled={isSaving} /></td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-3"><TagList tags={tags.sizes} onClick={setTagFilter} manual={tags.manual.sizes} /></td>
                      <td className="px-3 py-3"><TagList tags={tags.styles} onClick={setTagFilter} manual={tags.manual.styles} /></td>
                      <td className="px-3 py-3"><TagList tags={tags.rooms} onClick={setTagFilter} manual={tags.manual.rooms} /></td>
                    </>
                  )}
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditing(isEditing ? null : product.id)}
                      data-testid={`admin-mapping-edit-${product.id}`}
                      className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border ${
                        isEditing ? "bg-[#1B3B36] text-[#F9F8F6] border-[#1B3B36]" : "border-[#1B3B36] text-[#1B3B36] hover:bg-[#1B3B36] hover:text-[#F9F8F6]"
                      }`}
                    >
                      {isSaving ? <Loader2 className="animate-spin" size={11} /> : isEditing ? "Done" : "Edit tags"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-[#84968C]">No products match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-6 text-[12px] text-[#84968C] font-light max-w-3xl">
        <strong>Tip:</strong> Click <em>Edit tags</em> on any row to bulk-tag by hand. Manual tags override the auto-derived ones. Clear all manual tags on a row to fall back to auto.
      </p>
    </div>
  );
}

function TagList({ tags, onClick, manual }) {
  if (!tags || tags.length === 0) return <span className="text-[#B0AFA8]">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((t) => (
        <button
          key={t}
          onClick={() => onClick(t)}
          className={`text-[11px] px-2 py-0.5 border ${
            manual
              ? "border-[#B85742] text-[#B85742] hover:bg-[#B85742] hover:text-white"
              : "border-[#D8D4CC] hover:border-[#1B3B36] hover:bg-[#F0EDE5]"
          }`}
        >{t}</button>
      ))}
    </div>
  );
}

function InlineTagEditor({ testid, options, value, onChange, disabled }) {
  const set = value || [];
  const toggle = (opt) => {
    const next = set.includes(opt) ? set.filter((t) => t !== opt) : [...set, opt];
    onChange(next);
  };
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1">
      {options.map((opt) => {
        const active = set.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => toggle(opt)}
            data-testid={`${testid}-${opt.replace(/[^a-z0-9]+/gi, "-")}`}
            className={`text-[11px] px-2 py-0.5 border transition-colors ${
              active
                ? "bg-[#1B3B36] text-[#F9F8F6] border-[#1B3B36]"
                : "border-dashed border-[#D8D4CC] text-[#4A4A4A] hover:border-[#1B3B36]"
            } ${disabled ? "opacity-60 cursor-wait" : ""}`}
          >
            {active ? "✓ " : "+ "}{opt}
          </button>
        );
      })}
    </div>
  );
}

// ==================== CATEGORIES TAB ====================

function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const { reload: reloadPublic } = useCategories();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories");
      setItems(data.categories || []);
    } catch {
      toast.error("Could not load categories");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => setCreating({ slug: "", label: "", banner_title: "", banner_subtitle: "", order: items.length + 1 });

  const saveNew = async () => {
    if (!creating.label.trim()) { toast.error("Label is required"); return; }
    try {
      await api.post("/admin/categories", creating);
      toast.success("Category added");
      setCreating(null);
      broadcastCategoriesChanged();
      reloadPublic();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveExisting = async (originalSlug, updated) => {
    try {
      await api.put(`/admin/categories/${originalSlug}`, updated);
      toast.success("Saved");
      broadcastCategoriesChanged();
      reloadPublic();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (slug, count) => {
    if (count > 0) {
      toast.error(`Move or delete the ${count} product(s) in this category first.`);
      return;
    }
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/admin/categories/${slug}`);
      toast.success("Deleted");
      broadcastCategoriesChanged();
      reloadPublic();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <Loader2 className="animate-spin" />;

  return (
    <div data-testid="admin-categories-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="overline">Categories</div>
          <h2 className="mt-2 font-serif text-3xl text-[#1B3B36]">Manage shop categories</h2>
          <p className="mt-2 text-[13px] text-[#4A4A4A] font-light max-w-2xl">
            Categories power the filter chips on the Collections page and the extra links under the Shop Rugs mega-menu. Deleting is blocked while products still reference the category.
          </p>
        </div>
        <button data-testid="admin-add-category" onClick={startCreate} className="inline-flex items-center gap-2 bg-[#1B3B36] text-[#F9F8F6] px-5 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-[#132A26]">
          <Plus size={13} /> Add category
        </button>
      </div>

      {creating && (
        <CategoryCard
          category={creating}
          isNew
          onChange={setCreating}
          onSave={saveNew}
          onCancel={() => setCreating(null)}
        />
      )}

      <div className="space-y-4 mt-4">
        {items.map((c) => (
          <EditableCategory key={c.slug} category={c} onSave={saveExisting} onDelete={remove} />
        ))}
      </div>
    </div>
  );
}

function EditableCategory({ category, onSave, onDelete }) {
  const [draft, setDraft] = useState(category);
  const [count, setCount] = useState(null);
  useEffect(() => { setDraft(category); }, [category]);
  useEffect(() => {
    api.get("/categories").then((r) => {
      const found = (r.data.categories || []).find((x) => x.slug === category.slug);
      setCount(found?.count ?? 0);
    }).catch(() => setCount(0));
  }, [category.slug]);
  return (
    <CategoryCard
      category={draft}
      count={count}
      onChange={setDraft}
      onSave={() => onSave(category.slug, { ...draft, order: Number(draft.order) })}
      onCancel={() => setDraft(category)}
      onDelete={() => onDelete(category.slug, count || 0)}
    />
  );
}

function CategoryCard({ category, count, onChange, onSave, onCancel, onDelete, isNew }) {
  const set = (k, v) => onChange({ ...category, [k]: v });
  return (
    <div data-testid={`admin-category-${category.slug || "new"}`} className="border border-[#D8D4CC] bg-white p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Label"><input value={category.label || ""} onChange={(e) => set("label", e.target.value)} data-testid="admin-category-label" className={inp} placeholder="e.g. Wall Hangings" /></Field>
        <Field label={isNew ? "Slug (auto if empty)" : "Slug"}>
          <input value={category.slug || ""} onChange={(e) => set("slug", e.target.value)} data-testid="admin-category-slug" className={inp} placeholder="e.g. wall-hangings" />
        </Field>
        <Field label="Banner title (on /collections)"><input value={category.banner_title || ""} onChange={(e) => set("banner_title", e.target.value)} className={inp} /></Field>
        <Field label="Order (lower = first)"><input type="number" value={category.order || 0} onChange={(e) => set("order", e.target.value)} className={inp} /></Field>
        <Field label="Banner subtitle" span2><textarea rows={2} value={category.banner_subtitle || ""} onChange={(e) => set("banner_subtitle", e.target.value)} className={inp} /></Field>
      </div>
      <div className="flex items-center gap-3 mt-4">
        {count !== null && !isNew && (
          <span className="text-[12px] text-[#84968C]">{count} product{count === 1 ? "" : "s"} in this category</span>
        )}
        <div className="ml-auto flex gap-2">
          {!isNew && (
            <button onClick={onDelete} data-testid={`admin-category-delete-${category.slug}`} className="border border-[#B84A3E] text-[#B84A3E] px-3 py-2 text-[11px] uppercase tracking-[0.18em] hover:bg-[#B84A3E] hover:text-white">
              <Trash2 size={12} />
            </button>
          )}
          <button onClick={onCancel} className="border border-[#84968C] text-[#4A4A4A] px-4 py-2 text-[11px] uppercase tracking-[0.18em]">Reset</button>
          <button onClick={onSave} data-testid={`admin-category-save-${category.slug || "new"}`} className="inline-flex items-center gap-1 bg-[#1B3B36] text-[#F9F8F6] px-4 py-2 text-[11px] uppercase tracking-[0.18em]">
            <Save size={12} /> {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ==================== PAGE IMAGES TAB ====================

const PAGE_LABELS = {
  home: "Homepage",
  custom: "Custom Rugs page",
  about: "About / Our Story page",
  journal: "Journal page",
};
const PAGE_ORDER = ["home", "custom", "about", "journal"];

function PageImagesTab() {
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState("home");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/page-images");
      setPages(data.pages || {});
    } catch {
      toast.error("Could not load page images");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveSlot = async (key, url) => {
    try {
      await api.put(`/admin/page-images/${key}`, { url });
      toast.success("Image saved — refresh the storefront to see it");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const revertSlot = async (key) => {
    try {
      await api.delete(`/admin/page-images/${key}`);
      toast.success("Reverted to default");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    }
  };

  if (loading) return <div className="py-24 text-center text-[#84968C]"><Loader2 className="mx-auto animate-spin" /></div>;

  const availablePages = PAGE_ORDER.filter((p) => pages[p]);

  return (
    <div data-testid="admin-page-images-tab">
      <div className="mb-8">
        <div className="overline">Page Images</div>
        <h2 className="mt-2 font-serif text-3xl text-[#1B3B36]">Replace images across your site</h2>
        <p className="mt-2 text-[13px] text-[#4A4A4A] font-light max-w-2xl">
          Upload images for the homepage, Custom Rugs, About and Journal pages. Each slot is clearly labelled with where it appears on the site. Click <strong>Revert</strong> on any slot to drop your upload and go back to the current default. Changes appear on the storefront on the next page load.
        </p>
      </div>

      {/* Page tabs */}
      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#D8D4CC]">
        {availablePages.map((p) => (
          <button
            key={p}
            data-testid={`admin-page-tab-${p}`}
            onClick={() => setActivePage(p)}
            className={`px-5 py-3 text-[12px] uppercase tracking-[0.18em] -mb-px border-b-2 ${
              activePage === p ? "border-[#1B3B36] text-[#1B3B36]" : "border-transparent text-[#84968C] hover:text-[#1B3B36]"
            }`}
          >
            {PAGE_LABELS[p] || p} <span className="opacity-60 ml-1">({Object.values(pages[p] || {}).flat().length})</span>
          </button>
        ))}
      </div>

      {/* Sections for the active page */}
      {Object.entries(pages[activePage] || {}).map(([section, slots]) => (
        <div key={section} className="mb-10" data-testid={`admin-page-section-${activePage}-${section.replace(/\s+/g, "-").toLowerCase()}`}>
          <h3 className="font-serif text-xl text-[#1B3B36] mb-1">{section}</h3>
          <div className="h-px bg-[#D8D4CC] mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {slots.map((slot) => (
              <PageImageSlotCard key={slot.key} slot={slot} onSave={saveSlot} onRevert={revertSlot} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PageImageSlotCard({ slot, onSave, onRevert }) {
  const [tempUrl, setTempUrl] = useState(slot.is_overridden ? slot.url : "");
  // When slot changes from the parent (after refetch), sync local state
  useEffect(() => { setTempUrl(slot.is_overridden ? slot.url : ""); }, [slot.key, slot.url, slot.is_overridden]);

  const handleChange = (newUrl) => {
    setTempUrl(newUrl);
    if (newUrl) onSave(slot.key, newUrl);
  };

  const safeKey = slot.key.replace(/[^a-z0-9]+/gi, "-");
  return (
    <div data-testid={`admin-page-slot-${safeKey}`} className="bg-white border border-[#D8D4CC] p-3">
      <ImageUploader
        testid={`slot-${safeKey}`}
        value={tempUrl}
        defaultUrl={slot.default_url}
        onChange={handleChange}
        onClear={slot.is_overridden ? () => onRevert(slot.key) : undefined}
        aspect="aspect-[4/5]"
      />
      <div className="mt-3">
        <div className="text-[13px] font-medium text-[#1B3B36] leading-tight">{slot.label}</div>
        <div className="flex items-center justify-between mt-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#84968C]">{slot.section}</div>
          {slot.is_overridden ? (
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#1B3B36]">● custom</span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#B0AFA8]">default</span>
          )}
        </div>
      </div>
    </div>
  );
}
